/**
 * TechnologyDrawerHeader Component
 *
 * Displays the technology header section including name, version, and share action.
 * Pure presentation component - all state management handled by parent.
 *
 * Pattern follows AssetDrawerHeader for consistency across sections.
 */

import { ShareIconButton } from '@/components/actions';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import { OverflowText } from '@/components/OverflowText';
import { parseTechnologyVersion } from '@/sections/technology/utils/technologyUtils';
import { Technology } from '@/types';
import { getTechnologyDisplayProduct } from '@/utils/entities/technology.util';

export interface TechnologyDrawerHeaderProps {
  technology: Technology;
  onShareClick: () => void;
}

/**
 * TechnologyDrawerHeader renders the top section of the technology drawer with:
 * - Technology name with copy functionality
 * - Share button for URL copying
 * - Version display
 *
 * This is a pure presentation component with no internal state.
 */
export function TechnologyDrawerHeader({
  technology,
  onShareClick,
}: TechnologyDrawerHeaderProps) {
  const displayName = getTechnologyDisplayProduct(technology);
  const displayVersion = parseTechnologyVersion(technology.version, true);

  return (
    <div className="flex w-full items-center justify-between gap-4">
      <div className="flex max-w-full flex-col gap-1 overflow-hidden">
        <CopyToClipboard alwaysShowCopyIcon>
          <OverflowText
            className="text-xl font-bold text-nowrap"
            text={displayName}
          />
        </CopyToClipboard>
        <CopyToClipboard alwaysShowCopyIcon>
          <OverflowText className="text-nowrap" text={displayVersion} />
        </CopyToClipboard>
      </div>
      <div className="flex items-center gap-2">
        <ShareIconButton
          onShare={onShareClick}
          data-testid="share-technology"
        />
      </div>
    </div>
  );
}
